import { useEffect, useState } from "react";
import isEmpty from "lodash/isEmpty";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { parse, stringify } from "qs";

import {
  getUserFavoritedStrains,
  getUserFollowedDispensaries,
} from "@/api/requests/getUserFavorites";
import toggleFavoriteStrain from "@/api/requests/toggleFavoriteStrain";
import { Action, Category } from "@/constants/events";
import { SnackBarMessages, useSnackBar } from "@/context/SnackBarContext";
import useDomainCountryCode from "@/hooks/useDomainCountryCode";
import { useEventTracker } from "@/hooks/useEventTracker";
import { useDispatch, useSelector } from "@/redux/hooks";
import {
  getIsLoggedIn,
  getUserFavoriteStrains,
  getUserFollowDispensaries,
} from "@/redux/selectors/user";
import {
  loadFavorites,
  loadFavoritesResults,
  loadFollowedDispensariesResults,
} from "@/redux/slices/user";
import { generateSignUpUrl } from "@/utils/generateSsoUrls";

interface FavoriteStrainProps {
  doesUserFollow: boolean;
  strainId: number;
  favoriteGaLabel?: string;
  unFavoriteGaLabel?: string;
}

const useUserFavorites = () => {
  const dispatch = useDispatch();
  const isLoggedIn = useSelector(getIsLoggedIn);
  const countryCode = useDomainCountryCode();
  const favoritedStrains = useSelector(getUserFavoriteStrains);
  const followedDispensaries = useSelector(getUserFollowDispensaries);
  const [favoritesEnabled, setFavoritesEnabled] = useState(true);
  const { replace } = useRouter();
  const searchParams = useSearchParams() || "";
  const query = parse(searchParams?.toString());
  const path = usePathname() || "";

  const { publishEvent } = useEventTracker();

  const retrieveFavorites = async () => {
    dispatch(loadFavorites());

    const { strains, totalCount: strainsTotalCount } =
      await getUserFavoritedStrains(1).then((userFavoritedStrainData) => {
        if (userFavoritedStrainData.error) {
          // lets prevent user frustration by removing the favorite button
          // if we get an error back
          setFavoritesEnabled(false);
        }
        return userFavoritedStrainData;
      });

    dispatch(
      loadFavoritesResults({
        strains,
        totalCount: strainsTotalCount,
      }),
    );

    const { dispensaries, totalCount: dispensariesTotalCount } =
      await getUserFollowedDispensaries().then(
        (userFollowedDispensariesData) => {
          if (userFollowedDispensariesData.error) {
            setFavoritesEnabled(false);
          }
          return userFollowedDispensariesData;
        },
      );
    dispatch(
      loadFollowedDispensariesResults({
        dispensaries,
        totalCount: dispensariesTotalCount,
      }),
    );
  };

  const { addSnackBarItem } = useSnackBar();

  const favoriteStrain = async ({
    doesUserFollow,
    strainId,
    favoriteGaLabel = "Favorite Strain",
    unFavoriteGaLabel = "Unfavorite Strain",
    ...customDimensions
  }: FavoriteStrainProps) => {
    const response = await toggleFavoriteStrain(
      countryCode,
      doesUserFollow,
      strainId,
    );

    if (response !== "not signed in") {
      await retrieveFavorites();
    }
    if (response === "followed") {
      publishEvent({
        action: Action.click,
        category: Category.favorite,
        label: favoriteGaLabel,
        ...customDimensions,
      });
      addSnackBarItem(SnackBarMessages.favoriteAdded);
    } else if (response === "unfollowed") {
      addSnackBarItem(SnackBarMessages.favoriteRemoved);
      publishEvent({
        action: Action.click,
        category: Category.favorite,
        label: unFavoriteGaLabel,
        ...customDimensions,
      });
    } else if (response === "not signed in") {
      window.location.href = generateSignUpUrl(
        countryCode,
        `${path}?strainId=${strainId}&favoriteGaLabel=${favoriteGaLabel}&unFavoriteGaLabel=${unFavoriteGaLabel}`,
      );
    }
    return response;
  };

  const checkIfUserFollows = (slug: string) => {
    return favoritedStrains?.strains?.length
      ? favoritedStrains?.strains?.findIndex(
          (favStrain) => favStrain?.slug === slug,
        ) > -1
      : false;
  };

  const useInitiateFavorites = () => {
    useEffect(() => {
      if (query.strainId && typeof query.strainId === "string") {
        const { favoriteGaLabel, unFavoriteGaLabel } = query;
        favoriteStrain({
          doesUserFollow: false,
          favoriteGaLabel: favoriteGaLabel?.toString(),
          strainId: Number(query.strainId),
          unFavoriteGaLabel: unFavoriteGaLabel?.toString(),
        }).then(() => {
          const newQuery = query;
          delete newQuery.strainId;
          replace(isEmpty(newQuery) ? `${path}?${stringify(newQuery)}` : path);
        });
      }
    }, [query]);

    useEffect(() => {
      if (
        isLoggedIn &&
        (!favoritedStrains?.strains || !followedDispensaries?.dispensaries)
      ) {
        retrieveFavorites();
      }
    }, [isLoggedIn]);
  };

  return {
    checkIfUserFollows,
    favoriteStrain,
    favoritedStrains,
    favoritesEnabled,
    followedDispensaries,
    retrieveFavorites,
    useInitiateFavorites,
  };
};

export default useUserFavorites;
